import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Shield, ExternalLink, Twitter, Linkedin, Mail, Youtube, CheckCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { subscribeNewsletter } from '../services/api';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsSubscribing(true);
    try {
      const result = await subscribeNewsletter(email);
      setIsSubscribed(true);
      setEmail('');
      toast.success(result.message || 'Successfully subscribed to safety updates!');
    } catch (error) {
      toast.error('Failed to subscribe. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-400">
      {/* Newsletter Section */}
      <div className="border-b border-slate-800">
        <div className="container mx-auto px-6 py-12">
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-white mb-2">Stay Updated on Safety</h3>
            <p className="text-slate-400 mb-6">
              Get weekly safety tips, OSHA updates, and exclusive resources delivered to your inbox
            </p>
            
            {!isSubscribed ? (
              <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 flex-1"
                  required
                />
                <Button 
                  type="submit"
                  disabled={isSubscribing}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white whitespace-nowrap"
                >
                  {isSubscribing ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    'Subscribe'
                  )}
                </Button>
              </form>
            ) : (
              <div className="flex items-center justify-center gap-2 text-emerald-400">
                <CheckCircle className="w-5 h-5" />
                <span>You&apos;re subscribed! Check your inbox.</span>
              </div>
            )}
            
            <p className="text-xs text-slate-500 mt-3">No spam, unsubscribe anytime</p>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <Shield className="w-5 h-5 text-white" />
              </div>
              <div>
                <span className="text-xl font-bold text-white">WSafeTraining.com</span>
                <span className="block text-xs text-slate-300">Free OSHA Training</span>
                <span className="block text-[10px] text-slate-400">powered by wsafe.ai · patent pending ai technology</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Comprehensive OSHA safety training to protect your workplace. 
              A free resource from WSafeTraining.com.
            </p>
            <a 
              href="https://wsafe.ai" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 transition-colors text-sm"
            >
              Visit wsafe.ai <ExternalLink className="w-4 h-4" />
            </a>
          </div>

          {/* Course Links */}
          <div>
            <h4 className="text-white font-semibold mb-4">Course</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="#curriculum" className="hover:text-white transition-colors">Curriculum</a></li>
              <li><a href="#scenarios" className="hover:text-white transition-colors">Interactive Scenarios</a></li>
              <li><a href="#quiz" className="hover:text-white transition-colors">Quizzes</a></li>
              <li><a href="#faq" className="hover:text-white transition-colors">FAQ</a></li>
              <li><a href="/verify" className="hover:text-white transition-colors">Verify Certificate</a></li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-white font-semibold mb-4">Resources</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a 
                  href="https://www.osha.gov" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="hover:text-white transition-colors inline-flex items-center gap-1"
                >
                  OSHA Official Site <ExternalLink className="w-3 h-3" />
                </a>
              </li>
              <li><a href="#" className="hover:text-white transition-colors">Safety Blog</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Compliance Guides</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Downloadable Templates</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Webinars</a></li>
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-white font-semibold mb-4">Support</h4>
            <ul className="space-y-2 text-sm">
              <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Contact Us</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm">
            © {new Date().getFullYear()} WSafeTraining.com · powered by wsafe.ai (patent pending). All rights reserved.
          </p>
          
          <div className="flex items-center gap-4">
            <a href="#" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-emerald-600 transition-colors">
              <Twitter className="w-4 h-4" />
            </a>
            <a href="#" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-emerald-600 transition-colors">
              <Linkedin className="w-4 h-4" />
            </a>
            <a href="#" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-emerald-600 transition-colors">
              <Youtube className="w-4 h-4" />
            </a>
            <a href="#" className="w-9 h-9 rounded-full bg-slate-800 flex items-center justify-center hover:bg-emerald-600 transition-colors">
              <Mail className="w-4 h-4" />
            </a>
          </div>

          <a 
            href="https://app.emergent.sh" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 text-sm hover:text-white transition-colors"
          >
            <img 
              src="https://avatars.githubusercontent.com/in/1201222?s=120&u=2686cf91179bbafbc7a71bfbc43004cf9ae1acea&v=4" 
              alt="Emergent" 
              className="w-5 h-5 rounded"
            />
            Made with Emergent
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
